import { Check as CheckIcon } from '@mui/icons-material'
import {
  Step,
  StepConnector,
  stepConnectorClasses,
  StepContent,
  StepIconProps,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { CompanyOpeningPhase } from '@/types'
import { CurrentStep } from './utils'

type CompanyOpeningStatusProps = {
  currentStep: CurrentStep
  phases: CompanyOpeningPhase[]
}

export function CompanyOpeningStatus({
  currentStep,
  phases,
}: CompanyOpeningStatusProps) {
  const currentStepIndex = useMemo(
    () => phases.findIndex((phase) => phase.label === currentStep.step),
    [phases, currentStep.step],
  )

  const hasFinishedProcess = phases.every((phase) => phase.status === 'completed')

  return (
    <Stepper
      activeStep={hasFinishedProcess ? phases.length : currentStepIndex}
      orientation="vertical"
      connector={<StepConnectorRoot />}
    >
      {phases.map((phase) => (
        <Step key={phase.code}>
          <StepLabel
            slots={{ stepIcon: StepIcon }}
            sx={{ p: 0, '.Mui-disabled': { color: 'GrayText' } }}
          >
            <Typography variant="h6" lineHeight={1.2}>
              {phase.label}
            </Typography>
          </StepLabel>
          <StepContent
            sx={{
              ml: 1,
              pl: 2,
              borderLeft: (theme) => `2px solid ${theme.palette.grey[400]}`,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {currentStep.status}
            </Typography>
          </StepContent>
        </Step>
      ))}
    </Stepper>
  )
}

const StepConnectorRoot = styled(StepConnector)(({ theme }) => ({
  marginLeft: 8,
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.success.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: theme.palette.success.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    width: 2,
    backgroundColor: theme.palette.grey[400],
    border: 0,
  },
}))

const StepIconRoot = styled('div')<{
  ownerState: { completed?: boolean; active?: boolean }
}>(({ theme }) => ({
  border: `2px solid ${theme.palette.grey[400]}`,
  color: '#fff',
  width: 18,
  height: 18,
  fontSize: 16,
  padding: 4,
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        border: 0,
        backgroundColor: theme.palette.primary.main,
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        border: 0,
        backgroundColor: theme.palette.success.main,
      },
    },
  ],
}))

const StepIcon = (props: StepIconProps) => {
  const { active, completed, className } = props

  return (
    <StepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : <div />}
    </StepIconRoot>
  )
}
